import { useState, useRef, useEffect, useId } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { UserPlus, Search, X } from "lucide-react";
import { useContacts, type Contact } from "@/hooks/useContacts";

interface ContactSelectorProps {
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  contactId: string | undefined;
  onSelect: (contact: Contact) => void;
  onClear: () => void;
  onManualChange: (field: "name" | "email" | "phone", value: string) => void;
}

export function ContactSelector({
  clientName,
  clientEmail,
  clientPhone,
  contactId,
  onSelect,
  onClear,
  onManualChange,
}: ContactSelectorProps) {
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fieldId = useId();

  const { data: contacts } = useContacts(search.length >= 2 ? search : undefined);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // If a contact is selected, show the selected state
  if (contactId && !manualMode) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs font-medium">Client</Label>
          <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => { onClear(); setManualMode(false); setSearch(""); }}>
            <X className="h-3 w-3 mr-1" /> Change
          </Button>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-0.5">
          <p className="font-medium">{clientName}</p>
          {clientEmail && <p className="text-muted-foreground">{clientEmail}</p>}
          {clientPhone && <p className="text-muted-foreground">{clientPhone}</p>}
        </div>
      </div>
    );
  }

  // Manual entry mode — show separate fields
  if (manualMode) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs font-medium">New Client</Label>
          <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => { setManualMode(false); onClear(); setSearch(""); }}>
            <Search className="h-3 w-3 mr-1" /> Search Instead
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor={`${fieldId}-name`} className="text-xs font-medium">Client Name *</Label>
            <Input id={`${fieldId}-name`} value={clientName} onChange={(e) => onManualChange("name", e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${fieldId}-email`} className="text-xs font-medium">Email</Label>
            <Input id={`${fieldId}-email`} type="email" value={clientEmail} onChange={(e) => onManualChange("email", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${fieldId}-phone`} className="text-xs font-medium">Phone</Label>
            <Input id={`${fieldId}-phone`} value={clientPhone} onChange={(e) => onManualChange("phone", e.target.value)} />
          </div>
        </div>
      </div>
    );
  }

  // Search mode
  return (
    <div ref={wrapperRef} className="space-y-1.5 relative">
      <Label htmlFor={`${fieldId}-search`} className="text-xs font-medium">Client *</Label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          id={`${fieldId}-search`}
          placeholder="Search contacts by name, email, or phone..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
          onFocus={() => { if (search.length >= 2) setShowDropdown(true); }}
          className="pl-9"
        />
      </div>

      {showDropdown && search.length >= 2 && (
        <div className="absolute z-50 w-full bg-popover border border-border rounded-md shadow-md mt-1 max-h-60 overflow-y-auto">
          {contacts && contacts.length > 0 ? (
            <>
              {contacts.slice(0, 8).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex flex-col"
                  onClick={() => {
                    onSelect(c);
                    setShowDropdown(false);
                    setSearch("");
                  }}
                >
                  <span className="font-medium">{c.first_name} {c.last_name || ""}</span>
                  <span className="text-xs text-muted-foreground">{c.email}{c.phone ? ` · ${c.phone}` : ""}</span>
                </button>
              ))}
            </>
          ) : (
            <div className="px-3 py-3 text-sm text-muted-foreground">No contacts found</div>
          )}
          <div className="border-t">
            <button
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex items-center gap-2 text-primary"
              onClick={() => {
                setManualMode(true);
                setShowDropdown(false);
                // Pre-fill name from search
                onManualChange("name", search);
              }}
            >
              <UserPlus className="h-4 w-4" />
              Add New Client
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
